import { ActionDef, TableColDef, TableDefine } from './type'

import utils from '@/libs/utils'
import config from '@/config'
import ColType from './colType'
import permissionHelper from '../permissionHelper'

import iIcon from '@/components/base/iIcon.vue'
import ActionDropdown from './actionDropdown.vue'

const defaultCol:any = {
  align: 'center'
}

/**
 * 表格数据解析类
 */
export default class TableHelper<T> {
    private _view:any = undefined
    private _columns:TableColDef[] = []
    private _actions:ActionDef<T>[] = []

    private _normalActions:ActionDef<T>[] = []
    private _expandActions:ActionDef<T>[] = []
    private _actionWidth?:number = undefined

    private _eventTriggerEdit?:(index:number, newValue:T) => void = undefined

    /**
     * 默认操作，用于继承覆盖
     */
    defaultTableActions:Record<string, ActionDef<T>> = {
      view: {
        name: 'view',
        title: '查看',
        icon: 'md-eye',
        onClick: (_item:any, index?:number) => {}
      },
      modi: {
        name: 'modi',
        title: '编辑',
        icon: 'ios-create',
        onClick: (_item:any, index?:number) => {}
      },
      del: {
        name: 'del',
        title: '删除',
        color: 'red',
        modal: {
          message: '确定要删除这条数据吗？'
        },
        icon: 'md-trash',
        onClick: (_item:any, _index?:number) => {}
      }
    }

    constructor (data:TableDefine<T>, view:any) {
      if (data) {
        this._columns = this._tableCols(data.tableCols || [])
        this._actions = this._tableActions(data.actions || [])
        this._normalActions = this._actions.filter(action => {
          return !action.expand
        })
        this._expandActions = this._actions.filter(action => {
          return !!action.expand
        })
        this._actionWidth = data.actionWidth
      } else {
        this._columns = []
        this._actions = []
        this._normalActions = []
        this._expandActions = []
        this._actionWidth = undefined
      }
      this._view = view
    }

    /**
     * 编辑模式下触发更新
     * @param params
     * @param newValue
     */
    private _onTriggerColUpdate (key:string, params:any, newValue:any) {
      if (this._eventTriggerEdit &&
      typeof this._eventTriggerEdit === 'function') {
        if (typeof newValue === 'object' || params.row[key] !== newValue) {
          params.row[key] = newValue
          this._eventTriggerEdit(params.index, params.row)
        }
      }
    }

    /**
     * 过滤解析配置，生成view-design对应格式
     * @param tableCols
     * @returns
     */
    private _tableCols (tableCols:Array<TableColDef>) {
      const cols = tableCols.filter((tableCol:TableColDef) => {
        if (tableCol.show !== undefined) {
          if (typeof tableCol.show === 'boolean') {
            return tableCol.show
          }
          if (typeof tableCol.show === 'function') {
            return tableCol.show()
          }
        }
        return true
      })

      return cols.map((tableCol:TableColDef) => {
        let result:any = { ...tableCol }
        if (result.type && typeof result.type === 'object') {
          if (ColType[result.type.module]) {
            result = ColType[result.type.module](result, this._onTriggerColUpdate.bind(this))
          } else {
            delete result.type
          }
        } else {
          if (result.type && ['index', 'selection', 'expand', 'html'].indexOf(result.type) < 0) {
            delete result.type
          } else if (result.render === undefined) {
            result = ColType['text'](result, this._onTriggerColUpdate.bind(this))
          }
        }
        if (result.children && result.children.length > 0) {
          result.children = this._tableCols(result.children)
        }
        for (const key in defaultCol) {
          if (!result[key]) {
            result[key] = defaultCol[key]
          }
        }
        result.title = result.title ? result.title : result.name
        if (result.minWidth === undefined) {
          result.minWidth = result.title && result.title.length ? result.title.length * 20 : 50
        }
        if (result.render) {
          const oldRender = result.render
          result.render = (h:any, params:any) => {
            const editable = typeof tableCol.editable === 'function'
              ? tableCol.editable(params)
              : tableCol.editable
            const placeholder = typeof tableCol.placeholder === 'function'
              ? (tableCol.placeholder(params) || `请输入${result.title}`)
              : (tableCol.placeholder || `请输入${result.title}`)
            let output = oldRender(h, params, editable, placeholder)
            const required = result.required && typeof result.required === 'function'
              ? result.required(params.row) : result.required
            if (editable && required) {
              output = h('div', {
                attrs: {
                  class: 'od-table-required',
                  style: 'display:flex;align-items:center'
                }
              }, [
                h('span', {
                  attrs: {
                    class: 'od-table-required-dot',
                    style: 'color:red;margin-right:5px;'
                  }
                }, '*'),
                output
              ])
            }
            return output
          }
        }

        return result
      })
    }

    /**
     * 过滤操作的解析配置，过滤没有权限的数据
     * @param tableCols
     * @returns
     */
    private _tableActions (tableActions:Array<ActionDef<T>>) {
      const curActions = tableActions.filter((action:ActionDef<T>) => {
        if (action.disabled !== undefined) {
          if (typeof action.disabled === 'boolean') {
            return !action.disabled
          }
          if (typeof action.disabled === 'function') {
            return !action.disabled()
          }
        }
        return true
      }).filter((action) => {
        if (action.access) {
          return permissionHelper.hasAccess(action.access)
        }
        return true
      })

      return curActions.map((tableAction:any) => {
        let result:any = {}
        if (this.defaultTableActions[tableAction.name]) {
          result = utils.recordClone(this.defaultTableActions[tableAction.name])
        }
        for (const key in tableAction) {
          result[key] = tableAction[key]
        }
        result.title = result.title ? result.title : result.name
        return result
      })
    }

    /**
     * 动作点击操作
     * @param def
     * @param item
     */
    _onClickAction (def:ActionDef<T>, item:any, index:number) {
      if (def.modal) {
        this._view.$Modal.confirm({
          title: def.modal.title ? def.modal.title : '提醒',
          content: def.modal.message,
          onOk: () => {
            if (def.onClick) {
              def.onClick(item, index)
            }
          }
        })
      } else if (def.onClick) {
        def.onClick(item, index)
      }
    }

    /**
     * 渲染动作按钮的图标
     * @param h
     * @param def
     * @returns
     */
    private _renderActionIcon (h:any, def:ActionDef<T>) {
      if (def.icon) {
        return h(iIcon, {
          attrs: {
            type: def.icon,
            title: def.title ? def.title : def.name,
            color: def.color ? def.color : '#3593FF',
            size: def.size ? def.size : 26
          }
        })
      } else if (def.image) {
        return h('img', {
          attrs: {
            src: def.image,
            title: def.title ? def.title : def.name
          }
        })
      }
      return undefined
    }

    private _renderTableActions (h:any, params:any) {
      const actions = this._actions.filter((def) => {
        if (def.show !== undefined) {
          return typeof def.show === 'function' ? def.show(params.row) : def.show
        }
        return true
      })
      // 过滤actions，隐藏不可显示项
      const normalActions = actions.filter(action => {
        return !action.expand
      })
      const expandActions = actions.filter(action => {
        return !!action.expand
      })

      // 生成操作列表
      let result = normalActions.map((def) => {
        const iconRender = config.tableActionIcon ? this._renderActionIcon(h, def) : undefined
        return h('div', {
          attrs: {
            class: 'izs-btn-action'
          },
          on: {
            click: () => {
              this._onClickAction(def, params.row, params.index)
            }
          }
        }, [iconRender || h('span', {
          style: {
            color: def.color ? def.color : '#3593FF'
          }
        }, def.title ? def.title : def.name)])
      })

      if (expandActions.length > 0) {
        result.push(h(ActionDropdown, {
          attrs: {
            dataItems: expandActions.map(action => {
              action._dropDownClick = this._onClickAction.bind(this, action, params.row, params.index)
              return action
            })
          }
        }))
      }

      if (!config.tableActionIcon) {
        const newResult = []
        for (let i = 0; i < result.length; i++) {
          newResult.push(result[i])
          newResult.push(h('Divider', {
            attrs: {
              type: 'vertical'
            }
          }))
        }
        result = newResult.slice(0, newResult.length - 1)
      }

      return h('div', {
        attrs: {
          class: 'od-actionpane'
        }
      }, result)
    }

    getTableCols () {
      if (this._normalActions.length > 0) {
        let actionWidth = this._actionWidth
        if (actionWidth === undefined) {
          if (!config.tableActionIcon) {
            actionWidth = (this._expandActions.length > 0 ? 40 : 0)
            for (let i = 0; i < this._normalActions.length; i++) {
              const def = this._normalActions[i]
              const title = def.title ? def.title : def.name
              actionWidth += title.length * 25
            }
            actionWidth = actionWidth + 30
          } else {
            actionWidth = this._normalActions.length * 40 + (this._expandActions.length > 0 ? 40 : 0)
          }
        }
        this._columns.push({
          title: '操作',
          key: 'action',
          width: actionWidth,
          align: 'center',
          fixed: 'right',
          render: (h:any, params:any) => {
            return this._renderTableActions(h, params)
          }
        })
      }
      return this._columns
    }

    public bindRowEditEvent (cb:(index:number, newValue:T) => void) {
      this._eventTriggerEdit = cb
    }
}
